<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  api, type BillingProfile, type Invoice, type OptSpec, type Pricing, type Progress, type RunSummary, type Server,
  type SupportRequest, type User,
} from "./api";
import { go, href, route, type Tab } from "./router";
import Logo from "./components/Logo.vue";
import Icon, { type IconName } from "./components/Icon.vue";
import AuthGate from "./components/AuthGate.vue";
import Dashboard from "./components/Dashboard.vue";
import ServerList from "./components/ServerList.vue";
import ScanWizard from "./components/ScanWizard.vue";
import RunList from "./components/RunList.vue";
import RunDetail from "./components/RunDetail.vue";
import BillingList from "./components/BillingList.vue";
import AdminPanel from "./components/AdminPanel.vue";

const me = ref<User | null>(null);
const ready = ref(false);
const servers = ref<Server[]>([]);
const opts = ref<OptSpec[]>([]);
const runs = ref<RunSummary[]>([]);
const invoices = ref<Invoice[]>([]);
const support = ref<SupportRequest[]>([]);
const pricing = ref<Pricing | null>(null);
const billing = ref<BillingProfile | null>(null);
const wizard = ref(false);
const menu = ref(false);
// Komunikat z aktywacji konta (link z maila).
const banner = ref("");

// Bieżące zadanie skanu — jedno na sesję przeglądarki. Log na żywo podpina
// RunDetail tej naprawy, a pulpit pokazuje jej postęp.
const live = ref<{ jobId: string; runId: string; progress: Progress | null } | null>(null);

async function loadServers() {
  servers.value = (await api.get<{ servers: Server[] }>("/api/servers")).servers;
}
async function loadOptions() {
  opts.value = (await api.get<{ scan: OptSpec[] }>("/api/options")).scan;
}
async function loadRuns() {
  runs.value = (await api.get<{ runs: RunSummary[] }>("/api/runs")).runs;
}
async function loadBilling() {
  const [i, s] = await Promise.all([
    api.get<{ invoices: Invoice[] }>("/api/invoices"),
    api.get<{ requests: SupportRequest[] }>("/api/support"),
  ]);
  invoices.value = i.invoices;
  support.value = s.requests;
}
async function loadBillingProfile() {
  billing.value = (await api.get<{ profile: BillingProfile | null }>("/api/billing-profile").catch(() => ({ profile: null }))).profile;
}
async function loadPricing() {
  pricing.value = await api.get<Pricing>("/api/pricing").catch(() => null);
}
async function boot() {
  await Promise.all([loadServers(), loadOptions(), loadRuns(), loadBilling(), loadPricing(), loadBillingProfile()]);
}

async function onAuthed(u: User) {
  await boot();
  me.value = u;
  if (!location.hash) go("pulpit");
}

async function logout() {
  menu.value = false;
  try {
    await api.post("/api/auth/logout");
  } finally {
    me.value = null;
    go("pulpit");
  }
}

async function invite() {
  menu.value = false;
  const email = prompt("Zaproszenie dla (e-mail, opcjonalnie — puste = dla kogokolwiek):");
  if (email === null) return;
  try {
    const d = await api.post<{ code: string; expiresAt: string }>("/api/invites", { email: email || undefined });
    // Kod pokazujemy RAZ — w bazie jest tylko jego hash.
    alert(`Kod zaproszenia (pokazywany tylko raz):\n\n${d.code}\n\nWażny do: ${new Date(d.expiresAt).toLocaleString("pl")}`);
  } catch (e) {
    alert((e as Error).message);
  }
}

async function changePassword() {
  menu.value = false;
  const oldPass = prompt("Obecne hasło:");
  if (!oldPass) return;
  const newPass = prompt("Nowe hasło (min. 12 znaków):");
  if (!newPass) return;
  try {
    await api.post("/api/auth/password", { oldPassword: oldPass, newPassword: newPass });
    alert("Hasło zmienione. Pozostałe sesje zostały wylogowane.");
  } catch (e) {
    alert((e as Error).message);
  }
}

function onScanStarted(d: { jobId: string; runId: string }) {
  wizard.value = false;
  live.value = { jobId: d.jobId, runId: d.runId, progress: null };
  void loadRuns();
  go("naprawy", d.runId);
}

function onLiveProgress(p: Progress) {
  if (live.value) live.value.progress = p;
  const r = runs.value.find((x) => x.runId === live.value?.runId);
  if (r) r.progress = p;
}

async function onLiveDone() {
  live.value = null;
  await Promise.all([loadRuns(), loadBilling()]);
}

/**
 * Linki z maili: ?verify=TOKEN (aktywacja konta), ?run=ID, ?admin=1,
 * ?invoices=1 → odpowiednie zakładki. Po obsłużeniu czyścimy query, żeby
 * odświeżenie nie zużywało tokenu drugi raz.
 */
async function handleDeepLinks(): Promise<void> {
  const params = new URLSearchParams(location.search);
  const verify = params.get("verify");
  if (verify) {
    try {
      const d = await api.post<{ user: User }>("/api/auth/verify", { token: verify });
      me.value = d.user;
      banner.value = "Konto aktywowane — witaj w CleanOps!";
    } catch (e) {
      banner.value = "✗ " + (e as Error).message;
    }
  }
  let hash = location.hash;
  const run = params.get("run");
  if (run) hash = href("naprawy", run);
  if (params.get("admin") === "1") hash = href("admin");
  if (params.get("invoices") === "1") hash = href("rozliczenia");
  if (params.toString()) history.replaceState(null, "", location.pathname + hash);
  if (!location.hash) history.replaceState(null, "", location.pathname + href("pulpit"));
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

onMounted(async () => {
  try {
    me.value = (await api.get<{ user: User | null }>("/api/auth/me")).user;
  } catch {
    me.value = null;
  }
  await handleDeepLinks();
  if (me.value) await boot();
  ready.value = true;
});

interface TabDef { id: Tab; label: string; icon: IconName; admin?: boolean; badge?: () => number }
const tabs = computed<TabDef[]>(() =>
  ([
    { id: "pulpit" as Tab, label: "Pulpit", icon: "home" },
    {
      id: "naprawy" as Tab, label: "Naprawy", icon: "shield",
      badge: () => runs.value.filter((r) => r.mode === "quarantine" && r.status !== "running" && r.status !== "failed" && !r.feedback).length,
    },
    { id: "serwery" as Tab, label: "Serwery", icon: "server" },
    {
      id: "rozliczenia" as Tab, label: "Rozliczenia", icon: "receipt",
      badge: () => invoices.value.filter((i) => i.status === "issued").length,
    },
    { id: "admin" as Tab, label: "Admin", icon: "settings", admin: true },
  ] as TabDef[]).filter((t) => !t.admin || me.value?.isAdmin),
);
const activeTab = computed(() => (route.value.tab === "admin" && !me.value?.isAdmin ? "pulpit" : route.value.tab));
</script>

<template>
  <div v-if="!ready" class="empty">ładowanie…</div>

  <template v-else-if="!me">
    <p v-if="banner" class="banner">{{ banner }}</p>
    <AuthGate @authed="onAuthed" />
  </template>

  <template v-else>
    <header class="topbar" @click="menu = false">
      <a class="brand" :href="href('pulpit')"><Logo :size="30" /></a>

      <nav class="tabs" aria-label="Sekcje panelu">
        <a
          v-for="t in tabs" :key="t.id" :href="href(t.id)" class="tab" :class="{ on: activeTab === t.id }" :title="t.label" :aria-label="t.label"
        >
          <Icon :name="t.icon" :size="18" class="ti" /><span class="tl">{{ t.label }}</span>
          <span v-if="t.badge && t.badge() > 0" class="bdg">{{ t.badge() }}</span>
        </a>
      </nav>

      <span class="spacer" />
      <button type="button" class="small newscan" :disabled="!servers.length" title="Nowa naprawa" @click.stop="wizard = true"><Icon name="plus" /><span class="tl"> Nowa naprawa</span></button>
      <div class="user" @click.stop>
        <button type="button" class="sec small avatar" @click="menu = !menu">
          <span class="dot">{{ me.email[0]?.toUpperCase() }}</span>
          <span class="mail">{{ me.email }}</span> <Icon name="chevron-down" :size="14" />
        </button>
        <div v-if="menu" class="menu">
          <div class="who">{{ me.email }}<span v-if="me.isAdmin" class="pill acc">admin</span></div>
          <button v-if="me.isAdmin" type="button" class="link" @click="invite"><Icon name="mail" /> Zaproszenie dla klienta</button>
          <button type="button" class="link" @click="changePassword"><Icon name="key" /> Zmień hasło</button>
          <button type="button" class="link" @click="logout"><Icon name="log-out" /> Wyloguj</button>
        </div>
      </div>
    </header>
    <p v-if="banner" class="banner">{{ banner }}</p>

    <main @click="menu = false">
      <Dashboard
        v-if="activeTab === 'pulpit'"
        :email="me.email" :servers="servers" :runs="runs" :invoices="invoices" :support="support"
        :live="live" :billing="billing" @new-scan="wizard = true"
      />

      <template v-else-if="activeTab === 'naprawy'">
        <RunDetail
          v-if="route.id"
          :run-id="route.id" :pricing="pricing"
          :live-job-id="live && live.runId === route.id ? live.jobId : null"
          @restored="loadRuns" @changed="loadRuns(); loadBilling()" @done="onLiveDone"
        />
        <div v-else class="page">
          <div class="pagehead">
            <div>
              <h1>Naprawy</h1>
              <p class="muted">Każda naprawa jest odwracalna — kopie plików sprzed zmian trzymamy u siebie.</p>
            </div>
            <button type="button" :disabled="!servers.length" @click="wizard = true"><Icon name="plus" /> Nowa naprawa</button>
          </div>
          <div v-if="!runs.length" class="card"><div class="body empty">
            <div class="big"><Icon name="shield" :size="40" /></div>
            <p class="lead">Jeszcze nic nie naprawialiśmy</p>
            <p>Zacznij od trybu „tylko sprawdź” — niczego nie zmienia, tylko pokazuje, co jest na serwerze.</p>
          </div></div>
          <RunList v-else :runs="runs" />
        </div>
      </template>

      <div v-else-if="activeTab === 'serwery'" class="page">
        <div class="pagehead">
          <div>
            <h1>Serwery</h1>
            <p class="muted">Poświadczenia są szyfrowane (AES-256-GCM) i nigdy nie wracają do przeglądarki.</p>
          </div>
        </div>
        <ServerList :servers="servers" @changed="loadServers" />
      </div>

      <BillingList
        v-else-if="activeTab === 'rozliczenia'"
        :invoices="invoices" :requests="support" :pricing="pricing" :billing="billing"
        @reload="loadBilling" @billing-saved="billing = $event"
      />

      <AdminPanel v-else-if="activeTab === 'admin' && me.isAdmin" :pricing="pricing" />
    </main>

    <ScanWizard
      v-if="wizard"
      :servers="servers" :opts="opts" :has-billing="!!billing"
      @started="onScanStarted" @close="wizard = false"
    />

    <!-- Tylko z panelu klienta: skan trwa, a klient jest w innej zakładce. -->
    <a v-if="live && !(activeTab === 'naprawy' && route.id === live.runId)" class="livepill" :href="href('naprawy', live.runId)">
      <span class="spin" /> {{ live.progress?.label ?? "Naprawa w toku" }}
      <b v-if="live.progress && live.progress.total">{{ live.progress.done }}/{{ live.progress.total }}</b>
    </a>
  </template>
</template>

<style scoped>
.topbar {
  display: flex; align-items: center; gap: var(--s4); padding: 0 var(--s5); height: 58px;
  background: var(--panel); border-bottom: 1px solid var(--bd); position: sticky; top: 0; z-index: 10;
}
.brand { text-decoration: none; display: flex; align-items: center; margin-right: var(--s2); }
.tabs { display: flex; gap: 2px; height: 100%; }
.tab {
  display: flex; align-items: center; gap: 6px; padding: 0 14px; height: 58px; color: var(--mut);
  text-decoration: none; font-size: 13px; font-weight: 550; border-bottom: 2px solid transparent;
  transition: color .12s, border-color .12s;
}
.tab:hover { color: var(--fg); }
.tab.on { color: var(--fg); border-bottom-color: var(--acc); }
.ti { opacity: .85; }
.bdg { background: var(--acc); color: #fff; font-size: 10px; font-weight: 700; border-radius: 99px; padding: 1px 6px; min-width: 18px; text-align: center; }
.newscan { padding: 7px 14px; }
.user { position: relative; }
.avatar { display: flex; align-items: center; gap: 8px; padding: 4px 10px 4px 4px; }
.dot { width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center; background: linear-gradient(135deg, #5aa0ff, #2dd4bf); color: #fff; font-weight: 800; font-size: 12px; }
.menu {
  position: absolute; right: 0; top: calc(100% + 6px); background: var(--panel); border: 1px solid var(--bd2);
  border-radius: var(--r2); box-shadow: var(--shadow); min-width: 240px; padding: var(--s2); display: flex; flex-direction: column; gap: 2px; z-index: 20;
}
.who { font-size: 12px; color: var(--mut); padding: var(--s2) var(--s3); border-bottom: 1px solid var(--bd); margin-bottom: 4px; display: flex; justify-content: space-between; gap: 8px; align-items: center; }
.menu .link { text-align: left; padding: 8px 12px; font-size: 13px; color: var(--fg); border-radius: var(--r1); }
.menu .link:hover { background: var(--panel3); text-decoration: none; }
.banner { margin: var(--s3) auto 0; max-width: 1100px; padding: var(--s2) var(--s4); background: var(--ok-dim); color: var(--ok); border-radius: var(--r1); font-size: 13px; }
.pagehead { display: flex; align-items: center; justify-content: space-between; gap: var(--s4); flex-wrap: wrap; margin-bottom: var(--s4); }
.pagehead h1 { font-size: 22px; font-weight: 750; letter-spacing: -.02em; }
.pagehead p { margin: 4px 0 0; font-size: 13px; }
.empty .big { font-size: 34px; opacity: .6; margin-bottom: var(--s2); }
.empty .lead { color: var(--fg); font-weight: 600; margin: 0 0 var(--s2); }
.livepill {
  position: fixed; right: 20px; bottom: 20px; display: flex; align-items: center; gap: 8px; z-index: 30;
  background: var(--acc); color: #fff; text-decoration: none; font-size: 13px; font-weight: 600;
  padding: 10px 16px; border-radius: 99px; box-shadow: 0 10px 30px -8px rgba(0,0,0,.6);
}
.livepill b { opacity: .85; font-weight: 600; }
.spin { width: 12px; height: 12px; border: 2px solid #fff; border-right-color: transparent; border-radius: 50%; animation: rot .8s linear infinite; }
@keyframes rot { to { transform: rotate(360deg); } }
@media (max-width: 860px) {
  /* Na wąskim ekranie zakładki i „Nowa naprawa" to same ikony: etykiety
     rozjeżdżały pasek na dwa rzędy. Tytuł (title/aria-label) zostaje. */
  .topbar { height: 56px; padding: 0 var(--s3); gap: var(--s2); }
  .brand :deep(.word) { display: none; }
  .tabs { gap: 0; flex: 1; justify-content: space-around; }
  .tab { height: 56px; padding: 0 8px; }
  .tab .tl, .newscan .tl, .mail { display: none; }
  .ti { opacity: 1; }
  .newscan { padding: 7px 9px; }
  .bdg { position: absolute; transform: translate(10px, -14px); }
  .tab { position: relative; }
}
</style>
